/*
================================================================================
FILE HEADER — apps/studio-web/src/ir/playwright-codegen.ts
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                   | What Changed          |
|----|------------|-------------------|-----------------------|-----------------------|
| 1  | 2026-06-25 | Claude Sonnet 4.6 | Phase 1 prep          | IR → Playwright .spec |
| 2  | 2026-11-05 | Claude Opus 5.5   | Public hosting + rename | Visible name Plex → Scriptwave; hosted at scriptwave.qagreen.com |
|    |            |                   |                       | TypeScript codegen    |

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/components/RecorderPanel.tsx

EXPORTS COVERED
---------------
- generatePlaywrightSpec(ir) — converts a TestIR document to a Playwright .spec.ts string

STRATEGY → PLAYWRIGHT API MAPPING
-----------------------------------
a11yId      → page.getByTestId('x')
text        → page.getByText('x', { exact: true })
textContains→ page.getByText('x')
contentDesc → page.getByLabel('x')
className   → page.getByRole('button', { name: 'x' })  (parses "role[name]" format)
css         → page.locator('css=...')
xpath       → page.locator('xpath=...')

IMPORTANT POINTS
----------------
1. Sensitive steps (passwords) emit `process.env.SECRET` instead of the literal value.
2. The first candidate with the highest-priority strategy wins for locator generation.
3. Unknown step types emit a TODO comment so generated code still compiles.
================================================================================
*/

import type { TestIR, IRStep, Selector, Candidate } from '@plex/ir';

const KEY_MAP: Record<string, string> = {
  back:   'Escape',
  home:   'Home',
  enter:  'Enter',
  tab:    'Tab',
  delete: 'Delete',
};

// ── Selector → Playwright locator string ──────────────────────────────────

function candidateToLocator(c: Candidate): string {
  switch (c.strategy) {
    case 'a11yId':
      return `page.getByTestId(${q(c.value)})`;
    case 'text':
      return `page.getByText(${q(c.value)}, { exact: true })`;
    case 'textContains':
      return `page.getByText(${q(c.value)})`;
    case 'contentDesc':
      return `page.getByLabel(${q(c.value)})`;
    case 'className': {
      // "role[name]" → getByRole('role', { name: 'name' })
      const m = /^(\w+)\[(.+)\]$/.exec(c.value);
      if (m) return `page.getByRole(${q(m[1] ?? '')}, { name: ${q(m[2] ?? '')} })`;
      return `page.locator(${q(c.value)})`;
    }
    case 'css':
      return `page.locator(${q(c.value)})`;
    case 'xpath':
      return `page.locator(${q('xpath=' + c.value)})`;
    default:
      return `page.locator(${q('*')})`;
  }
}

function selectorToLocator(sel: Selector): string {
  // Pick the highest-priority (first) candidate
  const best = sel.candidates[0];
  if (!best) return `page.locator('*') /* ${sel.description} */`;
  return `${candidateToLocator(best)} /* ${sel.description} */`;
}

// ── Step → Playwright action line(s) ─────────────────────────────────────

function stepToLines(step: IRStep, indent: string): string[] {
  const i = indent;

  switch (step.type) {
    case 'openUrl':
      return [`${i}await page.goto(${q(step.url)});`];

    case 'tap':
      return [`${i}await ${selectorToLocator(step.selector)}.click();`];

    case 'doubleTap':
      return [`${i}await ${selectorToLocator(step.selector)}.dblclick();`];

    case 'longPress':
      return [
        `${i}await ${selectorToLocator(step.selector)}.hover();`,
        `${i}await page.mouse.down();`,
        `${i}await page.waitForTimeout(${(step as { durationMs?: number }).durationMs ?? 500});`,
        `${i}await page.mouse.up();`,
      ];

    case 'typeText': {
      const s = step as { text: string; sensitive?: boolean };
      const val = s.sensitive ? `process.env.SECRET ?? ''` : q(s.text);
      return [`${i}await page.keyboard.type(${val});`];
    }

    case 'clearText':
      return [
        `${i}await ${selectorToLocator((step as { selector: Selector }).selector)}.clear();`,
      ];

    case 'pressKey': {
      const key = KEY_MAP[(step as { key: string }).key] ?? (step as { key: string }).key;
      return [`${i}await page.keyboard.press(${q(key)});`];
    }

    case 'wait':
      return [`${i}await page.waitForTimeout(${(step as { durationMs: number }).durationMs});`];

    case 'waitFor': {
      const ws = step as { selector: Selector; state?: string; timeoutMs?: number };
      const state = ws.state ?? 'visible';
      const opts = ws.timeoutMs
        ? `{ state: ${q(state)}, timeout: ${ws.timeoutMs} }`
        : `{ state: ${q(state)} }`;
      return [`${i}await ${selectorToLocator(ws.selector)}.waitFor(${opts});`];
    }

    case 'waitForNetwork':
      return [`${i}await page.waitForLoadState('networkidle');`];

    case 'assert': {
      const as = step as { selector: Selector; assertion: string; value?: string };
      const loc = selectorToLocator(as.selector);
      switch (as.assertion) {
        case 'visible':    return [`${i}await expect(${loc}).toBeVisible();`];
        case 'notVisible': return [`${i}await expect(${loc}).toBeHidden();`];
        case 'enabled':    return [`${i}await expect(${loc}).toBeEnabled();`];
        case 'disabled':   return [`${i}await expect(${loc}).toBeDisabled();`];
        case 'hasText':    return [`${i}await expect(${loc}).toContainText(${q(as.value ?? '')});`];
        default:           return [`${i}await expect(${loc}).toBeVisible(); // ${as.assertion}`];
      }
    }

    case 'screenshot':
      return [`${i}await page.screenshot({ path: \`screenshot-\${Date.now()}.png\` });`];

    case 'scrollUntilVisible': {
      const sv = step as { selector: Selector };
      return [`${i}await ${selectorToLocator(sv.selector)}.scrollIntoViewIfNeeded();`];
    }

    case 'networkAssert': {
      const na = step as { urlPattern: string; method?: string; expectedStatus?: number; captureAs?: string };
      const varName = na.captureAs ?? 'response';
      const lines = [
        `${i}const ${varName} = await page.waitForResponse(r => r.url().includes(${q(na.urlPattern)})` +
          (na.method ? ` && r.request().method() === ${q(na.method)}` : '') + `);`,
      ];
      if (na.expectedStatus) {
        lines.push(`${i}expect(${varName}.status()).toBe(${na.expectedStatus});`);
      }
      return lines;
    }

    case 'setVariable': {
      const sv = step as { name: string; source: { kind: string; value?: string } };
      if (sv.source.kind === 'literal') {
        return [`${i}const ${sv.name} = ${q(sv.source.value ?? '')};`];
      }
      return [`${i}// setVariable '${sv.name}' from ${sv.source.kind} — implement as needed`];
    }

    case 'evaluate': {
      const ev = step as { left: string; operator: string; right?: string; storeAs?: string };
      return [`${i}// evaluate: ${ev.left} ${ev.operator} ${ev.right ?? ''}`];
    }

    case 'script': {
      const sc = step as { code: string; storeAs?: string };
      const assign = sc.storeAs ? `const ${sc.storeAs} = ` : '';
      return [`${i}${assign}await page.evaluate(\`${sc.code.replace(/`/g, '\\`')}\`);`];
    }

    case 'if': {
      const ifs = step as { condition: { selector: Selector; assertion: string }; then: IRStep[]; else?: IRStep[] };
      const loc = selectorToLocator(ifs.condition.selector);
      const lines: string[] = [];
      lines.push(`${i}if (await ${loc}.isVisible()) {`);
      for (const s of ifs.then) lines.push(...stepToLines(s, indent + '  '));
      if (ifs.else && ifs.else.length > 0) {
        lines.push(`${i}} else {`);
        for (const s of ifs.else) lines.push(...stepToLines(s, indent + '  '));
      }
      lines.push(`${i}}`);
      return lines;
    }

    case 'repeat': {
      const rs = step as { count: number; steps: IRStep[] };
      const lines: string[] = [];
      lines.push(`${i}for (let _i = 0; _i < ${rs.count}; _i++) {`);
      for (const s of rs.steps) lines.push(...stepToLines(s, indent + '  '));
      lines.push(`${i}}`);
      return lines;
    }

    case 'callFlow':
      return [`${i}// callFlow: ${(step as { sharedFlowName: string }).sharedFlowName} — extract to a helper function`];

    case 'print': {
      const ps = step as { message: string; level?: string };
      return [`${i}console.${ps.level ?? 'info'}(${q(ps.message)});`];
    }

    default:
      return [`${i}// TODO: unsupported step type '${step.type}'`];
  }
}

// ── Main export ───────────────────────────────────────────────────────────

export function generatePlaywrightSpec(ir: TestIR): string {
  const lines: string[] = [];

  // Header comment
  lines.push(`// Generated by Scriptwave Web Recorder — ${new Date().toISOString()}`);
  lines.push(`// Test: ${ir.meta.name}`);
  if (ir.meta.tags.length > 0) lines.push(`// Tags: ${ir.meta.tags.join(', ')}`);
  lines.push('');
  lines.push(`import { test, expect } from '@playwright/test';`);
  lines.push('');

  // Base URL from config
  if (ir.config.baseUrl) {
    lines.push(`// Base URL: ${ir.config.baseUrl}`);
    lines.push(`// Set in playwright.config.ts: use: { baseURL: '${ir.config.baseUrl}' }`);
    lines.push('');
  }

  // Before/after hooks
  if (ir.hooks.beforeEach.length > 0) {
    lines.push(`test.beforeEach(async ({ page }) => {`);
    for (const step of ir.hooks.beforeEach) lines.push(...stepToLines(step, '  '));
    lines.push(`});`);
    lines.push('');
  }

  // Main test
  lines.push(`test(${q(ir.meta.name)}, async ({ page }) => {`);
  lines.push(`  test.setTimeout(${ir.config.timeouts.suite});`);
  lines.push('');

  for (const flow of ir.flows) {
    if (ir.flows.length > 1) {
      lines.push(`  // Flow: ${flow.name}`);
    }
    for (const step of flow.steps) {
      const label = (step as { label?: string }).label;
      if (label) lines.push(`  // ${label}`);
      lines.push(...stepToLines(step, '  '));
    }
  }

  lines.push(`});`);

  if (ir.hooks.afterEach.length > 0) {
    lines.push('');
    lines.push(`test.afterEach(async ({ page }) => {`);
    for (const step of ir.hooks.afterEach) lines.push(...stepToLines(step, '  '));
    lines.push(`});`);
  }

  return lines.join('\n');
}

// ── Utility ───────────────────────────────────────────────────────────────

function q(s: string): string {
  return JSON.stringify(s);
}
